import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { firstValueFrom, BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { KEY_LOCAL_STORAGE } from '../../constants/storage';
import { TokenDTO, type LoginPayloadDTO, type LoginResponseDTO } from '../../dtos/login.dto';
import { type RequestPasswordResetDTO, type ResetPasswordDTO, type PasswordResetTokenResponseDTO, type ResetPasswordResponseDTO } from '../../dtos/password-reset.dto';
import { StorageService } from '../storage/storage.service';
import { HttpWrapperService } from '../http-wrapper/http-wrapper.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isRefreshing = false;
  private refreshPromise: Promise<TokenDTO> | null = null;

  constructor(
    private readonly jwtHelperService: JwtHelperService,
    private readonly httpClient: HttpWrapperService,
    private readonly storage: StorageService,
    private readonly router: Router,
    private readonly userService: UserService
  ) { }

  isAuthenticated(): boolean {
    const token = this.storage.get(KEY_LOCAL_STORAGE.TOKEN);
    // Aquí podrías verificar si el token está expirado o es válido
    if (token) {
      return !this.jwtHelperService.isTokenExpired(token?.accessToken);
    }
    return !!token;
  }

  login(body: LoginPayloadDTO): Promise<LoginResponseDTO> {
    return this.httpClient.post<LoginResponseDTO>(`${environment.url}/auth/backoffice/login`, body);
  }

  getToken(): TokenDTO {
    return this.storage.get(KEY_LOCAL_STORAGE.TOKEN);
  }

  logout() {
    this.storage.clear();
    this.userService.clearCurrentUser();
    this.router.navigate(['/login']);
  }

  decodeToken(): any {
    if (!this.getToken()?.accessToken) {
      return null;
    }
    return this.jwtHelperService.decodeToken(this.getToken()?.accessToken);
  }

  // Método para refrescar el token usando promesas
  async refreshToken(): Promise<TokenDTO> {
    if (this.isRefreshing && this.refreshPromise) {
      // Si ya se está refrescando, retornar la promesa en curso
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    const currentToken = this.storage.get(KEY_LOCAL_STORAGE.TOKEN);
    if (!currentToken?.refreshToken) {
      this.isRefreshing = false;
      throw new Error('No refresh token available');
    }

    const refreshData = {
      refreshToken: currentToken.refreshToken
    };

    this.refreshPromise = this.httpClient.post<TokenDTO>(`${environment.url}/auth/refresh-token`, { data: refreshData }).
      then((newToken: TokenDTO) => {
        this.storage.set(KEY_LOCAL_STORAGE.TOKEN, newToken);
        this.isRefreshing = false;
        this.refreshPromise = null;
        return newToken;
      }).catch((error) => {
        this.isRefreshing = false;
        this.refreshPromise = null;
        throw error;
      });

    return this.refreshPromise;
  }

  async refreshTokenIfNeeded(): Promise<TokenDTO | null> {
    const token = this.getToken();
    if (token && this.jwtHelperService.isTokenExpired(token.accessToken)) {
      try {
        return await this.refreshToken();
      } catch (error) {
        this.logout();
        return null;
      }
    }
    return token;
  }

  // Método para limpiar el token
  clearToken(): void {
    this.storage.removeItem(KEY_LOCAL_STORAGE.TOKEN);
  }

  // Métodos para recuperación de contraseña
  requestPasswordReset(body: RequestPasswordResetDTO): Promise<PasswordResetTokenResponseDTO> {
    return this.httpClient.post<PasswordResetTokenResponseDTO>(`${environment.url}/auth/request-password-reset`, body);
  }

  verifyPasswordResetToken(token: string): Promise<{ valid: boolean }> {
    return this.httpClient.post<{ valid: boolean }>(`${environment.url}/auth/verify-password-reset-token`, { token });
  }

  resetPassword(body: ResetPasswordDTO): Promise<ResetPasswordResponseDTO> {
    return this.httpClient.post<ResetPasswordResponseDTO>(`${environment.url}/auth/reset-password`, body);
  }
}
